/**
 * 统一的错误访问工具。
 *
 * 设计目标：业务代码不再需要 `catch (error: any)`，也不需要到处写
 * `error?.response?.data?.message` 这类不安全的链式访问。
 *
 * 所有导出函数都接受 `unknown`，内部做类型守卫，保证：
 *   1. 调用方始终有返回值可用（至少是 fallback 字符串）
 *   2. 调用方不会因为 error 形状不符合预期而崩溃
 *
 * 对应治理文档：docs/typescript-migration/conventions.md#1-错误处理
 */

/**
 * 后端（axios/umi-request）错误的最小形状。
 * 只列出业务代码真实会读取的字段。
 */
export interface BackendErrorShape {
  response?: {
    status?: number;
    data?: {
      message?: string;
      detail?:
        | string
        | Array<{
            msg?: string;
            message?: string;
            loc?: string[];
            field?: string;
          }>;
      code?: string;
      errorMessage?: string;
      msg?: string;
    };
  };
  request?: unknown;
  message?: string;
  name?: string;
  code?: string;
  config?: { url?: string };
}

/**
 * 判断一个未知错误是否具有"后端错误"的基本形状。
 *
 * 注意：这是一个"足够宽松"的守卫——只要具备 `response` 或 `message` 其一即可。
 * 用于避免在访问字段前写大量 `typeof === 'object'` 冗余检查。
 */
export function isBackendError(e: unknown): e is BackendErrorShape {
  return (
    typeof e === 'object' &&
    e !== null &&
    ('response' in e || 'message' in e || 'request' in e || 'code' in e)
  );
}

/**
 * 从错误对象中提取用户可读的错误信息。
 *
 * 查找顺序（与 src/requestErrorConfig.ts 的后端错误提取逻辑保持一致）：
 *   1. response.data.errorMessage
 *   2. response.data.message
 *   3. response.data.msg
 *   4. response.data.detail（字符串）
 *   5. response.data.detail[0].msg（FastAPI 验证错误数组）
 *   6. error.message
 *   7. fallback
 */
export function getErrorMessage(e: unknown, fallback = '操作失败'): string {
  if (!isBackendError(e)) {
    if (typeof e === 'string') return e;
    return fallback;
  }

  const data = e.response?.data;

  if (data) {
    if (data.errorMessage) return data.errorMessage;
    if (data.message) return data.message;
    if (data.msg) return data.msg;

    const detail = data.detail;
    if (typeof detail === 'string' && detail.length > 0) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      const msg = first?.msg || first?.message;
      if (msg) return msg;
    }
  }

  if (e.message) return e.message;
  return fallback;
}

/**
 * 提取 HTTP 状态码。
 * 常用于区分 404 / 409 / 422 等业务友好的错误分支。
 */
export function getErrorStatus(e: unknown): number | undefined {
  if (!isBackendError(e)) return undefined;
  return e.response?.status;
}

/**
 * 提取后端业务错误码（response.data.code 或 error.code）。
 * 典型用法：`if (getErrorCode(error) === 'CONFLICT') { ... }`
 */
export function getErrorCode(e: unknown): string | undefined {
  if (!isBackendError(e)) return undefined;
  return e.response?.data?.code ?? e.code;
}

/**
 * 判断是否是"请求被取消/中止"类错误。
 * 包括 axios cancel、fetch AbortError、以及项目特有的登出排水中止。
 *
 * 业务代码通常需要静默忽略这类错误，不弹 toast。
 */
export function isCancelError(e: unknown): boolean {
  if (!isBackendError(e)) return false;
  if (e.name === 'AbortError') return true;
  if (e.name === 'CanceledError') return true;
  if (e.name === 'LogoutDrainingError') return true;
  if (typeof e.message === 'string' && e.message.includes('logout-draining')) {
    return true;
  }
  if (e.message === 'LOGOUT_DRAINING') return true;
  return false;
}

/**
 * 提取 FastAPI 422 验证错误的详细字段信息（用于表单错误提示）。
 * 无验证详情时返回 undefined。
 */
export function getValidationDetails(
  e: unknown,
): Array<{ field: string; message: string }> | undefined {
  if (!isBackendError(e)) return undefined;
  const detail = e.response?.data?.detail;
  if (!Array.isArray(detail)) return undefined;

  const results = detail
    .map((item) => {
      const field = item?.loc?.join('.') || item?.field || '';
      const message = item?.msg || item?.message || '';
      if (!field && !message) return null;
      return { field, message };
    })
    .filter(
      (item): item is { field: string; message: string } => item !== null,
    );

  return results.length > 0 ? results : undefined;
}
